import React, { useCallback, useMemo, useState } from 'react';

import { LoanCalculatorContext } from './context';
import { pmt } from './services/pmt';
import styled from '@emotion/styled';
import { useStoreData } from '../../hooks';

export const LoanCalculator = ({
    fieldOneLabel,
    fieldTwoLabel,
    children,
}: any) => {
    const { scoreData } = useStoreData().user;

    /** @type {LoanCalculatorSettings} */
    const settings = scoreData.calculatorConfig;

    if (
        !settings ||
        !settings.stepSize ||
        !settings.mortgageDefault ||
        !settings.mortgageRate ||
        !settings.downpaymentDefaultMortgage ||
        !settings.consumerDefault ||
        !settings.consumerRate ||
        !settings.downpaymentDefaultConsumer ||
        !settings.downpaymentNewBankDefault ||
        !settings.newBankRate
    ) {
        throw Error('calculatorConfig is unavailable');
    }

    const {
        stepSize,
        mortgageDefault,
        mortgageRate,
        downpaymentDefaultMortgage,
        consumerDefault,
        consumerRate,
        downpaymentDefaultConsumer,
        newBankRate,
        downpaymentNewBankDefault,
    } = settings;

    const [fields, setFields] = useState<any>([
        {
            id: 'houseDebts',
            label: fieldOneLabel,
            value: mortgageDefault,
            step: stepSize,
        },
        {
            id: 'otherDebts',
            label: fieldTwoLabel,
            value: consumerDefault,
            step: stepSize,
        },
    ]);

    const getFieldValue = useCallback(
        (id) => fields.find((field: any) => field.id === id).value,
        [fields],
    );

    const handleFieldChange = useCallback(
        (id, value) => {
            const newArr = [...fields]; // copying the old datas array
            newArr.find((f) => f.id === id).value = value;

            setFields([...newArr]);
        },
        [fields],
    );

    const mortgageMonthly = pmt(
        mortgageRate / 100 / 12,
        downpaymentDefaultMortgage * 12,
        getFieldValue('houseDebts'),
    );

    const unsecuredMonthly = pmt(
        consumerRate / 100 / 12,
        downpaymentDefaultConsumer * 12,
        getFieldValue('otherDebts'),
    );

    const oldLoansMonthly = Math.abs(mortgageMonthly + unsecuredMonthly);
    const newBankMonthly = Math.abs(
        pmt(
            newBankRate / 100 / 12,
            downpaymentNewBankDefault * 12,
            getFieldValue('houseDebts') + getFieldValue('otherDebts'),
        ),
    );

    const monthlySavings = Math.round(oldLoansMonthly - newBankMonthly);
    const newMonthlyCost = Math.round(newBankMonthly);

    const contextValue = useMemo(
        () => ({
            fields,
            handleFieldChange,
            monthlySavings,
            newMonthlyCost,
            settings,
        }),
        [fields, handleFieldChange, monthlySavings, newMonthlyCost, settings],
    );

    return (
        <LoanCalculatorContext.Provider value={contextValue}>
            <Calculator>{children}</Calculator>
        </LoanCalculatorContext.Provider>
    );
};

const Calculator = styled.div`
    font-family: ${(props) => props.theme.headings.fontFamily};
    margin: 40px 0;
`;
